import type { ReplyRecipients } from '@/components/tickets/ReplyEditor';
import type { Attachment } from '@/components/tickets/FileAttachments';

/**
 * Undo send for email replies. On by default; set VITE_UNDO_SEND_ENABLED=false
 * to send replies immediately as before. When the backend has undo turned off
 * it answers with an immediate send, so the flag only needs to change to opt out.
 */
export function isUndoSendEnabled(): boolean {
  const flag = (import.meta.env.VITE_UNDO_SEND_ENABLED ?? '').toString().trim().toLowerCase();
  return flag !== 'false' && flag !== '0' && flag !== 'off';
}

/** RFC 4122 v4 id for one send. crypto.randomUUID needs a secure context, so fall back. */
export function newOperationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Insecure context — fall through.
    }
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The structured error code from a BFF error response, if any. */
export function undoErrorCode(error: unknown): string | undefined {
  const data = (error as { response?: { data?: any } })?.response?.data;
  if (!data) return undefined;
  if (typeof data.code === 'string') return data.code;
  if (data.error && typeof data.error === 'object' && typeof data.error.code === 'string') return data.error.code;
  if (data.message && typeof data.message === 'object' && typeof data.message.code === 'string') {
    return data.message.code;
  }
  return undefined;
}

export function undoErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** A human-readable message from a BFF error response. */
export function undoErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: any } })?.response?.data;
  const candidates = [data?.error?.message, typeof data?.message === 'string' ? data.message : undefined];
  return candidates.find((m) => typeof m === 'string' && m.trim().length > 0) ?? fallback;
}

/** Everything needed to put an undone reply back into the composer. */
export interface ReplySnapshot {
  content: string;
  recipients?: ReplyRecipients;
  mentions?: Array<{ userId: string; userName: string }>;
  /** The staged composer attachments, exactly as they were. */
  attachments: Attachment[];
}

/**
 * Local deadline for the countdown. The server's countdown started just before
 * its response reached us, so the local clock's `receivedAt + window` slightly
 * overstates it; prefer the server deadline when the two clocks roughly agree.
 */
export function localDeadline(serverDeadline: string | null, undoSeconds: number, receivedAt: number): number {
  const byWindow = receivedAt + Math.max(0, undoSeconds) * 1000;
  const parsed = serverDeadline ? Date.parse(serverDeadline) : NaN;
  if (Number.isFinite(parsed) && Math.abs(parsed - byWindow) < 5_000) return Math.min(parsed, byWindow);
  return byWindow;
}
